import { useState, useMemo } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import GoogleStreetViewCoverageMap from "../components/Map";
import StreetViewPanorama from "../components/StreetView";
import { TabbedPanel } from "../components/TabbedPanel";
import { ExportModal } from "../components/ExportModal";
import type { ILocation, ITag } from "@map-bldr/shared";
import { useLocations, useCreateLocation, useDeleteLocation, useUpdateLocation } from "../hooks/locationHooks";
import { usePrefetchLocationTagLinks, useCreateAndApplyTag } from "../hooks/locationTagLinkHooks";
import { useQueryClient } from "@tanstack/react-query";

export interface IBuilderProps {}

const DEFAULT_SV_LOC = {
  lat: 41.88850,
  lng: -87.62431,
  heading: 40,
  pitch: 12,
  zoom: 1,
};

export function Builder({}: IBuilderProps) {
  const queryClient = useQueryClient();

  // Query hooks - fetch data from database
  const { data: locations = [], isLoading: locationsLoading, error: locationsError } = useLocations();

  // Prefetch all location tag links
  usePrefetchLocationTagLinks();

  // Mutation hooks - modify data in database
  const createLocation = useCreateLocation();
  const deleteLocation = useDeleteLocation();
  const updateLocation = useUpdateLocation();
  const createAndApplyTag = useCreateAndApplyTag();

  // Local UI state (not persisted to database)
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);

  // Filter locations for map display based on selected tags (OR logic: show locations with ANY selected tag)
  // Always include the selected location, even if it doesn't match the filter
  const filteredLocations = useMemo(() => {
    if (selectedTagFilters.length === 0) return locations;

    return locations.filter(location => {
      const locationTagIds = queryClient.getQueryData<string[]>(['locationTagLinks', location.id]) || [];
      return location.id === selectedLocationId ||
        locationTagIds.some(tagId => selectedTagFilters.includes(tagId));
    });
  }, [locations, queryClient, selectedTagFilters, selectedLocationId]);

  // Compute current street view location from selected location
  const selectedLocation = locations.find(loc => loc.id === selectedLocationId);
  const currentStreetViewLocation: IStreetViewLocation = selectedLocation
    ? {
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        heading: selectedLocation.heading,
        pitch: selectedLocation.pitch,
        zoom: selectedLocation.zoom,
        panoId: selectedLocation.pano_id || null,
      }
    : DEFAULT_SV_LOC;

  const handleSelectLocation = (locationId: string) => {
    setSelectedLocationId(locationId);
  };

  const handleUnselectLocation = () => {
    setSelectedLocationId(null);
  };

  const handleTagFilterChange = (tagIds: string[]) => {
    setSelectedTagFilters(tagIds);
  };

  const handleAddLocation = (
    lat: number,
    lng: number,
    panoId?: string,
    panoDate?: string,
  ) => {
    // Create location in database - ID is generated by frontend
    const id = crypto.randomUUID();
    createLocation.mutate(
      {
        id,
        lat,
        lng,
        pano_id: panoId || '',
        heading: DEFAULT_SV_LOC.heading,
        pitch: DEFAULT_SV_LOC.pitch,
        zoom: DEFAULT_SV_LOC.zoom,
      },
      {
        onSuccess: (newLocation) => {
          // Set the newly created location as selected
          setSelectedLocationId(newLocation.id);
        },
      }
    );
  };

  const handleRemoveLocation = (id: string) => {
    // Delete location from database using database-generated UUID
    deleteLocation.mutate(id);
  };

  const handleCreateAndApplyTag = (locationId: string, name: string) => {
    // Create tag and associate with location in database
    // Frontend generates UUID for new tag
    const id = crypto.randomUUID();
    createAndApplyTag.mutate({
      locationId,
      tag: {
        id,
        name,
        description: '',
      },
    });
  };

  const handleUpdateSelectedLocation = (svLocation: IStreetViewLocation) => {
    if (!selectedLocationId) return;

    // Update the selected location in database with current StreetView state
    updateLocation.mutate({
      locationId: selectedLocationId,
      data: {
        lat: svLocation.lat,
        lng: svLocation.lng,
        heading: svLocation.heading,
        pitch: svLocation.pitch,
        zoom: svLocation.zoom,
        pano_id: svLocation.panoId || undefined,
      },
    });
  };

  // Handle loading and error states
  if (locationsLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100vw", height: "100vh" }}>
        <p className="font-mono text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (locationsError) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100vw", height: "100vh", color: "red" }}>
        Error loading data: {locationsError?.message}
      </div>
    );
  }

  return (
    <APIProvider apiKey={process.env.BUN_PUBLIC_GOOGLE_API_KEY || ""}>
      <div style={{ display: "flex", width: "100vw", height: "100vh" }}>
        <div style={{ width: "50%", height: "100%" }}>
          <GoogleStreetViewCoverageMap
            locations={filteredLocations}
            selectedLocationId={selectedLocationId}
            onAddLocation={handleAddLocation}
            onSelectLocation={handleSelectLocation}
          />
        </div>

        <div
          style={{
            width: "50%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ width: "100%", height: "50%" }}>
            <StreetViewPanorama
              onUpdate={handleUpdateSelectedLocation}
              svLocation={currentStreetViewLocation}
              disableUpdate={!selectedLocationId}
            />
          </div>
          <TabbedPanel
            selectedLocationId={selectedLocationId}
            selectedTagFilters={selectedTagFilters}
            onSelectLocation={handleSelectLocation}
            onUnselectLocation={handleUnselectLocation}
            onRemoveLocation={handleRemoveLocation}
            onCreateAndApplyTag={handleCreateAndApplyTag}
            onTagFilterChange={handleTagFilterChange}
            onExport={() => setShowExportModal(true)}
          />
        </div>
      </div>

      {showExportModal && (
        <ExportModal
          allLocations={locations}
          filteredLocations={filteredLocations}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </APIProvider>
  );
}

export default Builder;
